<script setup lang="ts">
import SettingsSection from '@/dashboard/vue/components/settings/SettingsSection.vue';
import { storeToRefs } from 'pinia';
import { useSettingsStore } from '@/dashboard/store/settingsStore';
import { watch } from 'vue';
import { useAppStore } from '@/dashboard/store/appStore';

const settingsStore = useSettingsStore();
const appStore = useAppStore();

const { currentUser } = storeToRefs(appStore);
const {
  usernameModel,
  emailModel,
  currentPasswordModel,
  passwordModel,
  passwordConfirmModel,
  emailError,
  currentPasswordError,
  passwordError,
  isSavedChanges,
} = storeToRefs(settingsStore);

function validateEmail(email: string) {
  const r = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
  return r.test(email);
}

watch([emailModel, usernameModel, passwordModel, isSavedChanges], () => {
  const isOpen = emailModel.value !== currentUser.value?.email || usernameModel.value !== currentUser.value.name || passwordModel.value.length > 0;
  settingsStore.setIsOpenSaveBanner(isSavedChanges.value ? isSavedChanges.value : isOpen);
});
</script>

<template>
  <div class="flex flex-col gap-y-s">
    <SettingsSection class="h-auto laptop:h-[208px]">
      <div class="flex flex-1 flex-col justify-between laptop:flex-row">
        <div class="mb-l flex flex-col laptop:mb-zero">
          <h5 class="mb-[5px] laptop:mb-[12px]">Name</h5>
          <p class="text-caption-s w-[200px] dark:text-gray-500 laptop:w-[240px]">The name that will be used in all workspaces and comments</p>
        </div>

        <div class="flex w-full items-center laptop:max-w-[480px]">
          <div class="tppr-text-field flex-1" :class="{ error: usernameModel.length <= 0 }">
            <p class="tppr-text-field__title text-gray-700 dark:text-gray-500">Your name</p>

            <div class="tppr-text-field__field-container">
              <input v-model="usernameModel" type="text" />
            </div>
          </div>
        </div>
      </div>
    </SettingsSection>

    <SettingsSection class="h-auto laptop:h-[208px]">
      <div class="flex flex-1 flex-col justify-between laptop:flex-row">
        <div class="mb-l flex flex-col laptop:mb-zero">
          <h5 class="mb-[5px] laptop:mb-[12px]">Email</h5>
        </div>

        <div class="flex w-full items-center laptop:max-w-[480px]">
          <div class="tppr-text-field flex-1" :class="{ error: !validateEmail(emailModel) || !!emailError }">
            <p class="tppr-text-field__title text-gray-700 dark:text-gray-500">Your email</p>

            <div class="tppr-text-field__field-container">
              <input v-model="emailModel" type="email" />
            </div>
            <p v-if="emailError" class="tppr-text-field__note">{{ emailError }}</p>
          </div>
        </div>
      </div>
    </SettingsSection>

    <SettingsSection class="h-auto laptop:h-[416px]">
      <div class="flex flex-1 flex-col justify-between laptop:flex-row">
        <div class="mb-l flex flex-col laptop:mb-zero">
          <h5 class="mb-[5px] laptop:mb-[12px]">Password</h5>
        </div>

        <div class="flex flex-1 flex-col items-end gap-[24px] pt-zero laptop:pt-[38px]">
          <div class="flex w-full items-center laptop:max-w-[480px]">
            <div
              class="tppr-text-field flex-1"
              :class="{ error: (currentPasswordModel.length <= 0 && passwordModel.length > 0) || !!currentPasswordError }"
            >
              <p class="tppr-text-field__title text-gray-700 dark:text-gray-500">Old password</p>

              <div class="tppr-text-field__field-container">
                <input v-model="currentPasswordModel" type="password" />
              </div>
              <p v-if="currentPasswordError" class="tppr-text-field__note">{{ currentPasswordError }}</p>
            </div>
          </div>

          <div class="flex w-full items-center laptop:max-w-[480px]">
            <div class="tppr-text-field flex-1" :class="{ error: passwordConfirmModel !== passwordModel || !!passwordError }">
              <p class="tppr-text-field__title text-gray-700 dark:text-gray-500">New password</p>

              <div class="tppr-text-field__field-container">
                <input v-model="passwordModel" type="password" />
              </div>
              <p v-if="passwordError" class="tppr-text-field__note">{{ passwordError }}</p>
            </div>
          </div>

          <div class="flex w-full items-center laptop:max-w-[480px]">
            <div class="tppr-text-field flex-1" :class="{ error: passwordConfirmModel !== passwordModel }">
              <p class="tppr-text-field__title text-gray-700 dark:text-gray-500">Retype new password</p>

              <div class="tppr-text-field__field-container">
                <input v-model="passwordConfirmModel" type="password" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </SettingsSection>
  </div>
</template>

<style scoped lang="scss"></style>
